import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useDatasetStore } from '@/store/dataset-store';
import { useUiStore } from '@/store/ui-store';
import { ColumnSettingsModal } from './ColumnSettingsModal';

/**
 * Sidebar entry point that opens {@link ColumnSettingsModal} for the
 * active dataset. Reads columns and current override map from the store.
 */
export function ColumnSettingsButton(): JSX.Element | null {
  const { t } = useTranslation();
  const activeId = useDatasetStore((s) => s.activeDatasetId);
  const datasets = useDatasetStore((s) => s.datasets);
  const open = useUiStore((s) => s.activeModal === 'column-settings');
  const setActiveModal = useUiStore((s) => s.setActiveModal);

  const entry = useMemo(
    () => (activeId ? datasets.get(activeId) : undefined),
    [activeId, datasets],
  );

  if (!entry) return null;

  const overrideCount = entry.overrides
    ? Object.keys(entry.overrides).length
    : 0;

  return (
    <>
      <button
        type="button"
        onClick={() => setActiveModal('column-settings')}
        className="w-full text-xs px-2.5 py-2 rounded-md hover:opacity-80 text-left flex items-center justify-between gap-2"
        style={{
          color: 'var(--color-fg)',
          background: 'var(--color-hover)',
          border: '1px solid var(--color-border)',
        }}
      >
        <span>{t('sidebar.button.columnSettings')}</span>
        {overrideCount > 0 && (
          <span
            className="text-[10px] px-1.5 rounded font-semibold"
            style={{
              background: 'var(--color-accent-subtle)',
              color: 'var(--color-accent)',
            }}
          >
            {overrideCount}
          </span>
        )}
      </button>
      {open && (
        <ColumnSettingsModal
          datasetId={entry.id}
          columns={entry.columns}
          current={entry.overrides ?? {}}
          onClose={() => setActiveModal(null)}
        />
      )}
    </>
  );
}
